import { CommandKind, CommandResult } from "@highmarch/domain";
import type { Command } from "@highmarch/domain";
import { motionOk } from "./kit";
import { toast } from "./toast";

/**
 * Refused actions (QoL I2): a command the sim turned down used to reach only the console. Now the button that sent it
 * shakes and a toast says why, in the player's words. The wording is keyed on what the player tried and why it was
 * refused; the sim's reasons are the rule, this only puts them into words.
 */
export function refusalText(command: Command, r: CommandResult): string {
  switch (r) {
    case CommandResult.NoRerollCharges: return "No rerolls left — skip for points, or pick a card";
    case CommandResult.InvalidPlacement: return command.kind === CommandKind.PickCard ? "No room for that road — pick another" : "Can't build there";
    case CommandResult.MaxTier: return "That tower is already at its top tier";
    case CommandResult.CannotSell: return "The fortress can't be demolished";
    // the sim locks the map while a wave runs: demolishing would reshape it under the enemies
    case CommandResult.RoadLocked: return command.kind === CommandKind.SellTower ? "Demolish between waves" : "The map is locked while a wave runs";
    case CommandResult.WrongPhase: return command.kind === CommandKind.SetRally ? "The flag works during a wave" : "Not now — finish the card in hand first";
    case CommandResult.NoOffer: return "No cards on the table";
    case CommandResult.NothingPicked: return "Pick a card first";
    case CommandResult.AlreadyPicked: return "A card is already in hand — place it or go BACK";
    case CommandResult.WrongCardKind: return "That card can't do that";
    case CommandResult.NoSuchTower: return "That tower is gone";
    case CommandResult.LevelOver: return "The battle is over";
    default: return "Can't do that now";
  }
}

/** How long (ms) after a press a refusal is still pinned on that button: the command runs within the same tap. */
export const PRESS_WINDOW_MS = 800;
/** How long the refusal toast stays up. */
export const REFUSAL_TOAST_MS = 2200;
/** The shake: a short horizontal wobble. */
const SHAKE_KEYS: Keyframe[] = [{ translate: "0 0" }, { translate: "-6px 0" }, { translate: "5px 0" }, { translate: "-3px 0" }, { translate: "2px 0" }, { translate: "0 0" }];
const SHAKE_MS = 320;

/**
 * Watches which button the player last pressed under `root` (capture, so nothing can swallow it) and, on a refusal,
 * shakes it and toasts the reason. One per level; `dispose` stops watching.
 */
export class RefusalFeedback {
  private last: HTMLElement | null = null;
  private at = -Infinity;
  private readonly onDown = (e: Event) => {
    const b = (e.target as Element | null)?.closest?.("button");
    if (b instanceof HTMLElement) { this.last = b; this.at = performance.now(); }
  };

  constructor(private readonly root: HTMLElement) { root.addEventListener("pointerdown", this.onDown, { capture: true, passive: true }); }

  refused(command: Command, r: CommandResult): void {
    const b = this.last && performance.now() - this.at <= PRESS_WINDOW_MS && this.last.isConnected ? this.last : null;
    if (b && motionOk() && typeof b.animate === "function") b.animate(SHAKE_KEYS, { duration: SHAKE_MS, easing: "ease-out" });
    toast(this.root, refusalText(command, r), REFUSAL_TOAST_MS);
  }

  dispose(): void { this.root.removeEventListener("pointerdown", this.onDown, { capture: true }); }
}
